/**
 * i18n (internationalization) system for Ключ-Астром MCP Chat Client
 * 
 * Provides translation support for Russian and English languages.
 */

import { ru, Translation } from './ru';
import { en } from './en';
import { useAppStore } from '@/stores/appStore';

export type Language = 'ru' | 'en';

const translations: Record<Language, Translation> = {
  ru,
  en,
};

/**
 * Get translations for the current language
 */
export function getTranslations(language: Language): Translation {
  return translations[language] || translations.ru; // Default to Russian
}

/**
 * Hook to access translations in components
 * 
 * Usage:
 * ```tsx
 * const { t, language } = useTranslation();
 * return <button>{t.common.save}</button>;
 * ```
 */
export function useTranslation() {
  const { settings } = useAppStore();
  const language = (settings.language || 'ru') as Language;
  const t = getTranslations(language);
  
  return { t, language };
}

/**
 * Get a translation string for a specific language (utility function)
 */
export function translate(key: string, language: Language = 'ru'): string {
  const t = getTranslations(language);
  const keys = key.split('.');
  let value: any = t;
  
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }
  
  return typeof value === 'string' ? value : key;
}

export { ru, en };
export type { Translation };
export { pluralize, formatCount, formatNumber } from './plural';
export type { PluralForms } from './plural';

